import React, { useState, useCallback, useEffect } from 'react';
import { EXTENSION_ID, MESSAGE_TYPES } from 'cfg/endpoints';
import { Loader } from 'components/Loader';
import { SERVICE_CALLS, clearCache } from 'extension-helpers';
import { DEFAULT_LANGUAGE_SETTING, EMAIL_FREQUENCY_SETTING, PROSPECT_RECORDING_SETTING, SettingsUpdate } from "interfaces/users";
import { Typography } from 'interfaces/typography';
import { useDispatch, useSelector } from 'react-redux';
import { getServicesManager } from 'services';
import { RootState } from 'store';
import { updateUser } from 'lib/redux/users/slice';
import { useUserData } from 'lib/redux/users/user-data-hook';
import { SettingsSection, SettingRow, StandardContainer } from 'utils/CommonUIComponents';

export enum CHROME_SETTINGS {
  TRELLUS_MODE = 'Lite Mode',
  TRELLUS_SIDE = 'Popup to right',
  EMBEDDED_COACHING = 'Embedded coaching',
  EMBEDDED_COACHING_NOT_HIGHLIGHT = 'Highlight embedded coaching?',
  DONT_FOCUS_PLATFORM_ON_CONNECT = 'Don\'t focus platform on connect',
}

export const CHROME_SETTINGS_TO_REQUEST_MESSAGE: {[k in CHROME_SETTINGS]: string} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_LITE_MODE_SETTING,
  [CHROME_SETTINGS.TRELLUS_SIDE]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_POPUP_DIRECTION_SETTING,
  [CHROME_SETTINGS.EMBEDDED_COACHING]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_EMBEDDED_COACHIG_ENABLED,
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_EMBEDDED_COACHING_NO_HIGHLIGHT,
  [CHROME_SETTINGS.DONT_FOCUS_PLATFORM_ON_CONNECT]: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_IS_DONT_FOCUS_PLATFORM_ON_CONNECT,
}

export const RESPONSE_TO_CHROME_SETTING_VALUE: {[k in CHROME_SETTINGS]: (response: any) => boolean} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: (response) => response && response['is_lite_mode_setting'],
  [CHROME_SETTINGS.TRELLUS_SIDE]: (response) => response &&  response['is_popup_to_right_setting'],
  [CHROME_SETTINGS.EMBEDDED_COACHING]: (response) => response &&  response['embedded_coaching'],
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: (response) => response && response['embedded_coaching_no_highlight'],
  [CHROME_SETTINGS.DONT_FOCUS_PLATFORM_ON_CONNECT]: (response) => response && response['dont_focus_platform_on_connect'],
}

export enum BACKEND_SETTINGS {
  DEFAULT_AUDIO_SETTINGS = 'Persist audio recording',
  LANGUAGE_SETTING = 'Default language',
  EMAIL_SETTINGS = 'Email settings',
  DATA_SEARCH_ENABLED = 'Disable LinkedIn finder?',
  DISABLE_SUPERHUMAN = 'Disable Superhuman?',
}

const SETTING_TOOLTIPS: {[key: string]: string} = {
  [CHROME_SETTINGS.TRELLUS_MODE]: 'Enable lite mode for a simplified coaching experience with fewer features',
  [CHROME_SETTINGS.TRELLUS_SIDE]: 'Position the coaching popup on the right side of your screen',
  [CHROME_SETTINGS.EMBEDDED_COACHING]: 'Show coaching insights directly embedded in your CRM interface',
  [CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT]: 'Highlight embedded coaching elements to make them more visible',
  [CHROME_SETTINGS.DONT_FOCUS_PLATFORM_ON_CONNECT]: 'Prevent automatically switching focus to the platform tab when connecting to a call',
  [BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS]: 'Choose which calls to automatically record by default',
  [BACKEND_SETTINGS.LANGUAGE_SETTING]: 'Set the primary language for transcription and coaching insights',
  [BACKEND_SETTINGS.EMAIL_SETTINGS]: 'Configure how often you receive email updates and summaries',
  [BACKEND_SETTINGS.DATA_SEARCH_ENABLED]: 'Disable automatic LinkedIn profile search and enrichment for prospects',
  [BACKEND_SETTINGS.DISABLE_SUPERHUMAN]: 'Disable LinkedIn Superhuman messaging feature',
}

type Setting = {
  backendSetting?: BACKEND_SETTINGS
  chromeSetting?: CHROME_SETTINGS
}

type SettingSection = {
  settings: Setting[]
  label: string
}

const DEFAULT_LANGUAGE_SETTING_TO_LABEL: {[key in DEFAULT_LANGUAGE_SETTING]: string} = {
  [DEFAULT_LANGUAGE_SETTING.ENGLISH]: 'English',
  [DEFAULT_LANGUAGE_SETTING.SPANISH]: 'Spanish',
  [DEFAULT_LANGUAGE_SETTING.FRENCH]: "French",
  [DEFAULT_LANGUAGE_SETTING.MULTI]: "Auto-detect",
}

const EMAIL_FREQUENCY_SETTING_TO_LABEL: {[key in EMAIL_FREQUENCY_SETTING]: string} = {
  [EMAIL_FREQUENCY_SETTING.DAILY]: 'Daily',
  [EMAIL_FREQUENCY_SETTING.TWICE_A_WEEK]: 'Twice a week',
  [EMAIL_FREQUENCY_SETTING.WEEKLY]: 'Weekly',
  [EMAIL_FREQUENCY_SETTING.NONE]: 'Never'
}

const PROSPECT_RECORDING_SETTING_TO_LABEL: {[key in PROSPECT_RECORDING_SETTING]: string} = {
  [PROSPECT_RECORDING_SETTING.ALL]: 'All calls',
  [PROSPECT_RECORDING_SETTING.NONE]: 'No calls',
  [PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY]: 'All 1-party calls',
  [PROSPECT_RECORDING_SETTING.DIALER]: "Mirror dialer settings"
}

export function UserSettings() {
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.user.value)
  const { team, userAuth } = useUserData()
  const [chromeSettings, setChromeSettings] = useState<Map<CHROME_SETTINGS, boolean>>(new Map())
  const [updatingSettings, setUpdatingSettings] = useState<Set<BACKEND_SETTINGS>>(new Set())
  const [passwordResetSent, setPasswordResetSent] = useState(false)
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false)

  const isChromeEnabled = () => { return typeof chrome !== 'undefined' && chrome.runtime !== undefined && chrome.runtime.sendMessage !== undefined }
  const [chromeEnabled, setChromeEnabled] = useState<boolean>(isChromeEnabled())

  const getChromeSettings = useCallback(async() => {
    if (!chromeEnabled) return
    for (const setting of Object.values(CHROME_SETTINGS)) {
      try {
        const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
        type: CHROME_SETTINGS_TO_REQUEST_MESSAGE[setting],
        })
        if (!response) {
          setChromeEnabled(false)
          return
        }
        setChromeSettings((prev) => {
          const newMap = new Map(prev)
          newMap.set(setting, RESPONSE_TO_CHROME_SETTING_VALUE[setting](response))
          return newMap
        })
      } catch (error) {
        setChromeEnabled(false)
        return
      }
    }
  }, [chromeEnabled])

  useEffect(() => {
    getChromeSettings()
  }, [getChromeSettings])

  const updateChromeSetting = (setting: CHROME_SETTINGS, value: boolean) => {
    let valueToSet = value
    switch (setting) {
      case CHROME_SETTINGS.TRELLUS_MODE:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_LITE_MODE,
          'is_lite_mode_setting': value,
          'coaching_page': false
        })
        break
      case CHROME_SETTINGS.TRELLUS_SIDE:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_POPUP_DIRECTION_IS_RIGHT,
          'is_popup_to_right_setting': value
        })
        break
      case CHROME_SETTINGS.EMBEDDED_COACHING:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_EMBEDDED_COACHING_ENABLED,
          'embedded_coaching': value,
        })
        break
      case CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT:
        valueToSet = !value
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_EMBEDDED_COACHING_NO_HIGHLIGHT,
          'embedded_coaching_no_highlight': valueToSet,
        })
        break
      case CHROME_SETTINGS.DONT_FOCUS_PLATFORM_ON_CONNECT:
        chrome.runtime.sendMessage(EXTENSION_ID, {
          'type': MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_SET_IS_DONT_FOCUS_PLATFORM_ON_CONNECT,
          'dont_focus_platform_on_connect': value,
        })
        break
    }
    setChromeSettings((prev) => {
      const newMap = new Map(prev)
      newMap.set(setting, valueToSet)
      return newMap
    })
  }

  const renderChromeSetting = (setting: CHROME_SETTINGS) => {
    let value = chromeSettings.get(setting) ?? false
    if (setting === CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT) value = (!value) && (chromeSettings.get(CHROME_SETTINGS.EMBEDDED_COACHING) ?? false)
    return <SettingRow label={setting} type="toggle" value={value} setValue={(x) => updateChromeSetting(setting, x as boolean)} tooltip={SETTING_TOOLTIPS[setting]} />
  }

  const updateSettingsResult = (updates: Map<BACKEND_SETTINGS, string | boolean>) => {
    const updatingKeys = Array.from(updates.keys())
    setUpdatingSettings(prev => new Set([...prev, ...updatingKeys]))

    const settingUpdate: SettingsUpdate = {
      'is_team': false
    }

    for (const [key, value] of updates) {
      switch (key) {
        case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS: settingUpdate['default_prospect_recording'] = value as PROSPECT_RECORDING_SETTING; break
        case BACKEND_SETTINGS.LANGUAGE_SETTING: settingUpdate['default_language'] = value as DEFAULT_LANGUAGE_SETTING; break
        case BACKEND_SETTINGS.EMAIL_SETTINGS: settingUpdate['email_frequency'] = value as EMAIL_FREQUENCY_SETTING; break
        case BACKEND_SETTINGS.DATA_SEARCH_ENABLED: settingUpdate['linkedin_finder_disabled'] = value as boolean; break
        case BACKEND_SETTINGS.DISABLE_SUPERHUMAN: settingUpdate['superhuman_disabled'] = value as boolean; break
      }
    }

    getServicesManager().postSettings(settingUpdate).then((success) => {
      if (success && user) {
        let updatedUser = { ...user }
        for (const [key, value] of updates) {
          switch (key) {
            case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
              updatedUser.default_prospect_recording = value as PROSPECT_RECORDING_SETTING;
              break
            case BACKEND_SETTINGS.LANGUAGE_SETTING:
              updatedUser.default_language = value as DEFAULT_LANGUAGE_SETTING;
              break
            case BACKEND_SETTINGS.EMAIL_SETTINGS:
              updatedUser.email_frequency_setting = value as EMAIL_FREQUENCY_SETTING;
              break
            case BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
              updatedUser.linkedin_finder_disabled = value as boolean;
              break
            case BACKEND_SETTINGS.DISABLE_SUPERHUMAN:
              updatedUser.superhuman_disabled = value as boolean;
              break
          }
        }
        dispatch(updateUser(updatedUser))
      }
    }).catch((error) => {
      console.error('Failed to update settings:', error)
    }).finally(() => {
      setUpdatingSettings(prev => {
        const newSet = new Set(prev)
        updatingKeys.forEach(key => newSet.delete(key))
        return newSet
      })
    })

    clearCache(SERVICE_CALLS.USER_SETTING)
  }

  const getSettingValue = (setting: BACKEND_SETTINGS): any => {
    switch (setting) {
      case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
        return user?.default_prospect_recording ?? PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY
      case BACKEND_SETTINGS.LANGUAGE_SETTING:
        return user?.default_language ?? DEFAULT_LANGUAGE_SETTING.ENGLISH
      case BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
        return user?.linkedin_finder_disabled ?? false
      case BACKEND_SETTINGS.DISABLE_SUPERHUMAN:
        return user?.superhuman_disabled ?? false
      case BACKEND_SETTINGS.EMAIL_SETTINGS:
        return user?.email_frequency_setting ?? EMAIL_FREQUENCY_SETTING.DAILY
      default:
        return null
    }
  }

  const renderBackendSetting = (setting: BACKEND_SETTINGS) => {
    const isLoading = updatingSettings.has(setting)

    switch (setting) {
      case BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
        const value = getSettingValue(setting)
        return <SettingRow label={setting}
          type="select"
          options={Object.values(PROSPECT_RECORDING_SETTING).map((v) => {return {label: PROSPECT_RECORDING_SETTING_TO_LABEL[v], value: v}})}
          value={value}
          setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
          tooltip={SETTING_TOOLTIPS[setting]}
          disabled={isLoading}
          loading={isLoading}
          />
      case BACKEND_SETTINGS.LANGUAGE_SETTING:
        return <SettingRow
        label={setting}
        type="select"
        options={Object.values(DEFAULT_LANGUAGE_SETTING).map((v) => {return {label: DEFAULT_LANGUAGE_SETTING_TO_LABEL[v], value: v}})}
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      case BACKEND_SETTINGS.EMAIL_SETTINGS:
        return <SettingRow label={setting}
          type="select"
          options={Object.values(EMAIL_FREQUENCY_SETTING).map((v) => {return {label: EMAIL_FREQUENCY_SETTING_TO_LABEL[v], value: v}})}
          value={getSettingValue(setting)}
          setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
          tooltip={SETTING_TOOLTIPS[setting]}
          disabled={isLoading}
          loading={isLoading}
          />
      case BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
        return <SettingRow label={setting} type="toggle"
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      case BACKEND_SETTINGS.DISABLE_SUPERHUMAN:
        return <SettingRow label={setting} type="toggle"
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      default:
        return null
   }
  }

  const renderSetting = (setting: Setting) => {
    if (setting.chromeSetting) return renderChromeSetting(setting.chromeSetting)
    if (setting.backendSetting) return renderBackendSetting(setting.backendSetting)
    return null
  }

  const renderSettingSection = (section: SettingSection) => {
    return (
      <SettingsSection title={section.label}>
        {section.settings.map((setting, index) => (
          <div key={index}>
            {renderSetting(setting)}
          </div>
        ))}
      </SettingsSection>
    )
  }

  const getPersonalSettingsSections = (): SettingSection[] => {
    const personalSections: SettingSection[] = []

    // Extension settings (always personal)
    if (chromeEnabled) {
      personalSections.push({
        label: 'Extension Settings',
        settings: [
          { chromeSetting: CHROME_SETTINGS.TRELLUS_MODE },
          { chromeSetting: CHROME_SETTINGS.TRELLUS_SIDE },
          { chromeSetting: CHROME_SETTINGS.EMBEDDED_COACHING },
          { chromeSetting: CHROME_SETTINGS.EMBEDDED_COACHING_NOT_HIGHLIGHT },
          { chromeSetting: CHROME_SETTINGS.DONT_FOCUS_PLATFORM_ON_CONNECT },
        ]
      })
    }

    // Personal audio settings
    personalSections.push({
      label: 'Audio Settings',
      settings: [
        { backendSetting: BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS },
        { backendSetting: BACKEND_SETTINGS.LANGUAGE_SETTING },
      ]
    })

    // Personal data & search settings
    personalSections.push({
      label: 'Data & Search',
      settings: [
        { backendSetting: BACKEND_SETTINGS.DATA_SEARCH_ENABLED },
        { backendSetting: BACKEND_SETTINGS.DISABLE_SUPERHUMAN },
      ]
    })

    // Personal email settings
    personalSections.push({
      label: 'Email Settings',
      settings: [
        { backendSetting: BACKEND_SETTINGS.EMAIL_SETTINGS },
      ]
    })

    return personalSections
  }

  const handlePasswordReset = async () => {
    if (!userAuth?.email) return

    setSendingPasswordReset(true)
    try {
      const success = await getServicesManager().requestPasswordResetEmail(userAuth.email)
      if (success) {
        setPasswordResetSent(true)
        setTimeout(() => setPasswordResetSent(false), 5000)
      }
    } catch (error) {
      console.error('Failed to send password reset email:', error)
    } finally {
      setSendingPasswordReset(false)
    }
  }

  const getSubscriptionTypeLabel = (subscriptionType: string | undefined) => {
    // Check if user has individual paid access
    if (user?.autodialer_paid && subscriptionType !== 'TEAM') {
      return 'Individual Paid Plan'
    }

    switch (subscriptionType) {
      case 'FREE': return 'Free Plan'
      case 'BETA': return 'Beta Plan'
      case 'TEAM': return 'Team Plan'
      default: return 'Free Plan'
    }
  }

  const renderUserProfile = () => {
    return (
      <SettingsSection title="Account Information">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Typography variant="mediumParagraph" color="#374151">Name</Typography>
            <Typography variant="mediumParagraph" color="#6b7280">{user?.user_name || 'Unknown User'}</Typography>
          </div>

          {userAuth?.email && (
            <div className="flex justify-between items-center">
              <Typography variant="mediumParagraph" color="#374151">Email</Typography>
              <Typography variant="mediumParagraph" color="#6b7280">{userAuth.email}</Typography>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Typography variant="mediumParagraph" color="#374151">Subscription</Typography>
            <Typography variant="mediumParagraph" color="#6b7280">{getSubscriptionTypeLabel(team?.subscription_type)}</Typography>
          </div>

          <div className="flex justify-between items-center">
            <Typography variant="mediumParagraph" color="#374151">Change Password</Typography>
            <button
              onClick={handlePasswordReset}
              disabled={sendingPasswordReset || !userAuth?.email}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Typography variant="smallParagraph" color="white">
                {sendingPasswordReset ? "Sending..." : passwordResetSent ? "Email Sent!" : "Reset Password"}
              </Typography>
            </button>
          </div>

          {passwordResetSent && (
            <div className="text-sm text-green-600">
              Password reset email sent! Check your inbox for instructions.
            </div>
          )}
        </div>
      </SettingsSection>
    )
  }

  const renderExtensionBanner = () => {
    if (chromeEnabled) return null

    return (
      <div className="w-full mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <Typography variant="mediumParagraph" color="#1e40af" fontWeight="500">
                  Install the Chrome Extension
                </Typography>
                <Typography variant="smallParagraph" color="#3730a3">
                  Enable additional features including the dialer and enhanced coaching capabilities
                </Typography>
              </div>
            </div>
            <div className="flex-shrink-0">
              <a
                href="https://app.trellus.ai/install"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Install Extension
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) return <Loader />

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="mb-4">
          <Typography variant="h2" color="#1F2937" fontWeight={600}>
            User Settings
          </Typography>
          <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '0.25rem' }}>
            Manage your personal preferences and account settings
          </Typography>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <StandardContainer>
          {renderExtensionBanner()}
          <div className="flex flex-col gap-6">
            {renderUserProfile()}
            {getPersonalSettingsSections().map((section, index) => (
              <div key={index}>
                {renderSettingSection(section)}
              </div>
            ))}
          </div>
        </StandardContainer>
      </div>
    </div>
  )
}